import { parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from '../providers/locale-provider';

/**
 * Formats a phone number for display using international format
 * @param phone - The phone number to format
 * @param countryCode - The country code for the phone number
 * @param fallbackText - Text to display when phone is empty (default: 'Not set')
 * @returns Formatted phone number or fallback text
 */
export function formatPhoneNumber(phone: string, countryCode: CountryCode = 'AU', fallbackText = 'Not set'): string {
  if (!phone) return fallbackText;
  try {
    const phoneNumber = parsePhoneNumberFromString(phone, countryCode);
    return phoneNumber?.isValid() ? phoneNumber.format('INTERNATIONAL') : phone;
  } catch {
    return phone;
  }
}

/**
 * Formats phone input as user types with Australian number patterns
 * @param phone - The phone number input
 * @param countryCode - The country code for formatting
 * @returns Formatted phone input string
 */
export function formatPhoneInput(phone: string, countryCode: CountryCode = 'AU'): string {
  if (!phone) return '';
  // Remove any non-numeric characters for input formatting
  const cleaned = phone.replace(/\D/g, '');

  // For Australian numbers - format based on first 2-4 digits
  if (countryCode === 'AU' && cleaned.length > 0) {
    // 1300 numbers: 1300 XXX XXX
    if (cleaned.startsWith('1300')) {
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 7) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 10)}`;
    }

    // 1800 numbers: 1800 XXX XXX
    if (cleaned.startsWith('1800')) {
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 7) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7, 10)}`;
    }

    // 13XX numbers: 13XX XX XX
    if (cleaned.startsWith('13') && cleaned.length >= 4) {
      if (cleaned.length <= 4) return cleaned;
      if (cleaned.length <= 6) return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
      return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)}`;
    }

    // Mobile numbers 04XX XXX XXX
    if (cleaned.startsWith('04') || (cleaned.startsWith('4') && cleaned.length >= 9)) {
      // Ensure we have the leading 0 for mobile numbers
      const withLeadingZero = cleaned.startsWith('4') && !cleaned.startsWith('04') ? `0${cleaned}` : cleaned;

      if (withLeadingZero.length <= 4) return withLeadingZero;
      if (withLeadingZero.length <= 7) return `${withLeadingZero.slice(0, 4)} ${withLeadingZero.slice(4)}`;
      return `${withLeadingZero.slice(0, 4)} ${withLeadingZero.slice(4, 7)} ${withLeadingZero.slice(7, 10)}`;
    }

    // Landline numbers 0X XXXX XXXX (02, 03, 07, 08)
    if (cleaned.startsWith('02') || cleaned.startsWith('03') || cleaned.startsWith('07') || cleaned.startsWith('08')) {
      if (cleaned.length <= 2) return cleaned;
      if (cleaned.length <= 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
      if (cleaned.length <= 10) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 6)} ${cleaned.slice(6)}`;
      return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 6)} ${cleaned.slice(6, 10)}`;
    }

    // Numbers that might be missing leading 0 for landlines (2, 3, 7, 8)
    if (cleaned.startsWith('2') || cleaned.startsWith('3') || cleaned.startsWith('7') || cleaned.startsWith('8')) {
      const withLeadingZero = `0${cleaned}`;
      if (withLeadingZero.length <= 2) return withLeadingZero;
      if (withLeadingZero.length <= 6) return `${withLeadingZero.slice(0, 2)} ${withLeadingZero.slice(2)}`;
      if (withLeadingZero.length <= 10)
        return `${withLeadingZero.slice(0, 2)} ${withLeadingZero.slice(2, 6)} ${withLeadingZero.slice(6)}`;
      return `${withLeadingZero.slice(0, 2)} ${withLeadingZero.slice(2, 6)} ${withLeadingZero.slice(6, 10)}`;
    }

    // Fallback for other numbers
    return cleaned;
  }

  return phone;
}
